import type { Locale, MessageKey } from '@autho/i18n';
import { catalogues, isLocale, negotiateLocale } from '@autho/i18n';
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { IntlProvider, useIntl } from 'react-intl';

type Theme = 'system' | 'light' | 'dark';

interface Preferences {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

const PreferencesContext = createContext<Preferences | null>(null);

// Per-browser conveniences only; the saved account locale lives on the membership (API).
function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function write(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Storage can be unavailable (private mode); preferences then last for the session only.
  }
}

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const stored = read('autho.locale');
    return isLocale(stored) ? stored : negotiateLocale(navigator.languages ?? []);
  });
  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = read('autho.theme');
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  });

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = () =>
      document.documentElement.classList.toggle(
        'dark',
        theme === 'dark' || (theme === 'system' && media.matches),
      );
    apply();
    media.addEventListener('change', apply);
    return () => media.removeEventListener('change', apply);
  }, [theme]);

  const setLocale = useCallback((next: Locale) => {
    write('autho.locale', next);
    setLocaleState(next);
  }, []);
  const setTheme = useCallback((next: Theme) => {
    write('autho.theme', next);
    setThemeState(next);
  }, []);

  const value = useMemo(
    () => ({ locale, setLocale, theme, setTheme }),
    [locale, setLocale, theme, setTheme],
  );

  return (
    <PreferencesContext.Provider value={value}>
      <IntlProvider locale={locale} defaultLocale="en" messages={catalogues[locale]}>
        {children}
      </IntlProvider>
    </PreferencesContext.Provider>
  );
}

export function usePreferences() {
  const ctx = useContext(PreferencesContext);
  if (!ctx) throw new Error('usePreferences outside PreferencesProvider');
  return ctx;
}

/** Typed message lookup: an unknown key is a compile error, not a blank string. */
export function useT() {
  const intl = useIntl();
  return useCallback(
    (id: MessageKey, values?: Record<string, string | number>) =>
      intl.formatMessage({ id }, values),
    [intl],
  );
}
